import { IconButton, Drawer } from '@mui/material';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Notification_Category,
  Notification_ReadState,
} from '@wavingroup/iot-core/gen/ts/wavin/blaze/v1/notification_pb';
import { NotificationDetails } from '~/pages/layout/components/NotificationDrawer/NotificationDetails';
import { useNotifications } from '~/pages/layout/components/NotificationDrawer/hooks/useNotifications';
import { useSetNotificationReadState } from '~/pages/layout/components/NotificationDrawer/hooks/useSetNotificationReadState';
import { NotificationList } from './NotificationList';
import { NotificationTrigger } from './NotificationTrigger';
import { NotificationFilters } from './NotificationFilters';
import { ParsedNotification } from './types';
import { useParsedNotifications } from './hooks/useParsedNotification';
import { listDevicesQuery } from '~/shared/api/listDevices.queries';
import { useSetNotificationReadStateForUserBulk } from './hooks/useSetNotificationReadStateForUserBulk';
import { FilterIcon } from '~/shared/components/Icons/Icons';
import { NotificationOptionsDialog } from './NotificationOptionsDialog';

interface NotificationsDrawerProps {
  hasNewNotifications?: boolean;
}

export function NotificationsDrawer({
  hasNewNotifications = false,
}: NotificationsDrawerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedNotification, setSelectedNotification] =
    useState<ParsedNotification | null>(null);
  const [activeFilter, setActiveFilter] = useState<Notification_Category>(
    Notification_Category.UNSPECIFIED,
  );
  const [deviceFilterName, setDeviceFilterName] = useState('');
  const [isOptionsDialogOpen, setIsOptionsDialogOpen] = useState(false);

  const {
    notifications,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    globalCategoryCounts,
  } = useNotifications(
    Notification_ReadState.UNSPECIFIED,
    activeFilter,
    deviceFilterName,
  );

  const { mutate: setReadState } = useSetNotificationReadState();
  const {
    mutate: setReadStateForUserBulk,
    isPending: isSetReadStateForUserBulkPending,
  } = useSetNotificationReadStateForUserBulk();
  const { data: devicesListModel } = useQuery(listDevicesQuery);
  const { t } = useTranslation();
  const parsedNotifications = useParsedNotifications(notifications, t);
  const deviceOptions = (devicesListModel?.devices ?? []).map((device) => ({
    name: device.name,
    label: device.title || device.serialNumber || device.name,
  }));
  const toggleReadState = (
    name: string,
    currentReadState: Notification_ReadState,
  ) => {
    if (currentReadState === Notification_ReadState.READ) return;

    setReadState({
      name,
      readState: Notification_ReadState.READ,
    });
  };

  const handleOpen = () => setIsOpen(true);

  const handleClose = () => {
    setIsOpen(false);
    setSelectedNotification(null);
  };

  const handleMarkFilteredAsRead = () => {
    setReadStateForUserBulk({
      readState: Notification_ReadState.READ,
      categoryFilter: activeFilter,
      filterDeviceName: deviceFilterName,
    });
  };

  const handleOpenOptionsDialog = () => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }

    setIsOptionsDialogOpen(true);
  };

  return (
    <>
      <NotificationTrigger
        hasNewNotifications={hasNewNotifications}
        onClick={handleOpen}
      />

      <Drawer
        anchor="right"
        open={isOpen}
        onClose={handleClose}
        sx={{
          '& .MuiDrawer-paper': {
            width: { xs: '100%', sm: 390 },
            backgroundColor: '#FAFAFA',
            pr: 0,
          },
        }}
      >
        {selectedNotification ? (
          <NotificationDetails
            title={selectedNotification.title}
            deviceTitle={selectedNotification.deviceTitle}
            roomTitle={selectedNotification.roomTitle}
            alertDescription={selectedNotification.alertDescription}
            stepsToPerform={selectedNotification.stepsToPerform}
            type={selectedNotification.category}
            typeLabel={selectedNotification.categoryLabel}
            date={selectedNotification.date}
            resolvedDate={selectedNotification.resolvedDate}
            urgency={selectedNotification.urgency}
            isResolved={selectedNotification.isResolved}
            onBack={() => setSelectedNotification(null)}
            onClose={handleClose}
          />
        ) : (
          <NotificationList
            isLoading={isLoading}
            notifications={parsedNotifications}
            onClose={handleClose}
            onMarkAsRead={toggleReadState}
            onSeeDetails={setSelectedNotification}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            onLoadMore={fetchNextPage}
            headerAction={
              <IconButton
                aria-label={t('notifications.options.title')}
                onClick={handleOpenOptionsDialog}
              >
                <FilterIcon fontSize="small" sx={{ color: 'primary.dark' }} />
              </IconButton>
            }
            filterBar={
              <NotificationFilters
                activeFilter={activeFilter}
                globalCategoryCounts={globalCategoryCounts}
                onFilterChange={(filter) => setActiveFilter(filter)}
              />
            }
          />
        )}
      </Drawer>
      <NotificationOptionsDialog
        open={isOptionsDialogOpen}
        onClose={() => setIsOptionsDialogOpen(false)}
        deviceFilterName={deviceFilterName}
        deviceOptions={deviceOptions}
        onDeviceFilterNameChange={setDeviceFilterName}
        onMarkAllAsRead={handleMarkFilteredAsRead}
        isMarkAllAsReadPending={isSetReadStateForUserBulkPending}
      />
    </>
  );
}
